import React, { useEffect, useMemo, useState } from "react";
import { Global } from "../helpers/Global";

// ✅ Toast global
import { useToast } from "../hooks/useToast";

// ===== MUI =====
import {
  Box,
  Paper,
  Typography,
  Stack,
  Button,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Switch,
  FormControlLabel,
  Avatar,
  MenuItem,
} from "@mui/material";
import Autocomplete from "@mui/material/Autocomplete";
import { DataGrid } from "@mui/x-data-grid";

export const GestionUsuarios = () => {
  // =========================
  // Config login (DNI vs MAIL)
  // =========================
  // "dni" | "mail"
  const PRIMARY_FIELD = (Global.loginPrimary || "dni").toLowerCase();
  const REQUIRE_MAIL = PRIMARY_FIELD === "mail";
  const REQUIRE_DNI = PRIMARY_FIELD === "dni";

  // =========================
  // Hooks / estado base
  // =========================
  const toast = useToast();

  const [usuarios, setUsuarios] = useState([]);
  const [usuariosLocal, setUsuariosLocal] = useState([]);
  const [loading, setLoading] = useState(true);

  // =========================
  // Filtros
  // =========================
  const [filtroRol, setFiltroRol] = useState("");
  const [filtroManager, setFiltroManager] = useState(null); // {value,label} o null
  const [mostrarDesactivados, setMostrarDesactivados] = useState(false);
  const [filtroUsuario, setFiltroUsuario] = useState(null); // {value,label} o null

  // =========================
  // Ordenación (DataGrid la gestiona, pero guardamos state si quieres)
  // =========================
  const [orden, setOrden] = useState({ campo: "nombre", ascendente: true });

  // =========================
  // Modales
  // =========================
  const [modalRol, setModalRol] = useState(false);
  const [modalManager, setModalManager] = useState(false);
  const [modalEditar, setModalEditar] = useState(false);

  // =========================
  // Selección + edición
  // =========================
  const [usuarioSeleccionado, setUsuarioSeleccionado] = useState(null);

  const [nuevoRol, setNuevoRol] = useState("");
  const [nuevoManager, setNuevoManager] = useState(""); // id o ""
  const [nuevoApellidos, setNuevoApellidos] = useState("");
  const [nuevoDni, setNuevoDni] = useState("");
  const [nuevoTelefono, setNuevoTelefono] = useState("");
  const [nuevoNombre, setNuevoNombre] = useState("");
  const [nuevoEmail, setNuevoEmail] = useState("");

  const [errorEmail, setErrorEmail] = useState("");
  const [errorMensaje, setErrorMensaje] = useState("");
  const [avatarFileEdit, setAvatarFileEdit] = useState(null);

  const [saving, setSaving] = useState(false);

  // =========================
  // Carga inicial
  // =========================
  useEffect(() => {
    fetchUsuarios();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchUsuarios = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${Global.url}usuarios/todos`, {
        headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
      });

      if (!res.ok) {
        toast.error("No se pudieron cargar los usuarios.");
        setUsuarios([]);
        setUsuariosLocal([]);
        return;
      }

      const data = await res.json().catch(() => []);
      const procesados = resolverManagers(Array.isArray(data) ? data : []);
      setUsuarios(procesados);
      setUsuariosLocal(procesados);
    } catch (error) {
      console.error("Error al obtener usuarios:", error);
      toast.error("Error de conexión al cargar usuarios.");
      setUsuarios([]);
      setUsuariosLocal([]);
    } finally {
      setLoading(false);
    }
  };

  const resolverManagers = (lista) => {
    const mapNombre = Object.fromEntries(
      (lista || []).map((u) => [u.id, `${u.nombre} ${u.apellidos || ""}`.trim()])
    );
    return (lista || []).map((u) => ({
      ...u,
      manager: u.gerente_id ? mapNombre[u.gerente_id] || "" : "",
    }));
  };

  // =========================
  // Helpers avatar
  // =========================
  const getAvatarUrl = (usuario) => {
    if (!usuario?.avatar_url) return null;
    // avatar_url viene como "uploads/avatars/xxx.jpg"
    return `${Global.url}${usuario.avatar_url}`;
  };

  const getIniciales = (usuario) => {
    const nombre = (usuario?.nombre || "").trim();
    const apellidos = (usuario?.apellidos || "").trim();
    const n1 = nombre ? nombre.split(" ")[0][0] : "";
    const a1 = apellidos ? apellidos.split(" ")[0][0] : "";
    const combo = (n1 + a1).toUpperCase();
    if (combo) return combo;
    if (nombre) return nombre[0].toUpperCase();
    return "?";
  };

  // =========================
  // Opciones filtros (Autocomplete)
  // =========================
  const rolOptions = useMemo(
    () => [
      { value: "", label: "Todos" },
      { value: "usuario", label: "Usuario" },
      { value: "gerente", label: "Gerente" },
      { value: "admin", label: "Administrador" },
    ],
    []
  );

  const managerOptions = useMemo(() => {
    const gerentes = (usuariosLocal || []).filter((u) => u.rol === "gerente");
    const uniq = new Map();
    gerentes.forEach((g) => uniq.set(String(g.id), `${g.nombre} ${g.apellidos || ""}`.trim()));
    return [
      { value: "", label: "Todos" },
      { value: "none", label: "Sin manager" },
      ...Array.from(uniq.entries())
        .map(([id, label]) => ({ value: String(id), label }))
        .sort((a, b) => a.label.localeCompare(b.label)),
    ];
  }, [usuariosLocal]);

  const usuarioOptions = useMemo(() => {
    return [
      { value: "", label: "Todos" },
      ...(usuariosLocal || []).map((u) => ({
        value: String(u.id),
        label: `${u.nombre} ${u.apellidos || ""} (${u.email || u.dni || ""})`.trim(),
      })),
    ].sort((a, b) => a.label.localeCompare(b.label));
  }, [usuariosLocal]);

  const selectedRolOption =
    rolOptions.find((o) => String(o.value) === String(filtroRol)) || rolOptions[0];

  // =========================
  // Filtros / Orden
  // =========================
  const usuariosFiltrados = useMemo(() => {
    const rol = filtroRol || "";
    const managerVal = filtroManager?.value ?? "";
    const usuarioVal = filtroUsuario?.value ?? "";

    return (usuariosLocal || []).filter((u) => {
      const byRol = !rol || u.rol === rol;

      let byManager = true;
      if (managerVal === "none") byManager = !u.gerente_id;
      else if (managerVal) byManager = Number(u.gerente_id) === Number(managerVal);

      const byActivo = mostrarDesactivados ? true : u.activo !== false;

      const byUsuario = usuarioVal ? String(u.id) === String(usuarioVal) : true;

      return byRol && byManager && byActivo && byUsuario;
    });
  }, [usuariosLocal, filtroRol, filtroManager, mostrarDesactivados, filtroUsuario]);

  const usuariosOrdenados = useMemo(() => {
    return [...usuariosFiltrados].sort((a, b) => {
      let valA = a[orden.campo] ?? "";
      let valB = b[orden.campo] ?? "";

      if (orden.campo === "manager") {
        valA = a.manager || "";
        valB = b.manager || "";
      }

      if (typeof valA === "string" && typeof valB === "string") {
        return orden.ascendente
          ? valA.localeCompare(valB, undefined, { sensitivity: "base" })
          : valB.localeCompare(valA, undefined, { sensitivity: "base" });
      }
      return orden.ascendente ? Number(valA) - Number(valB) : Number(valB) - Number(valA);
    });
  }, [usuariosFiltrados, orden]);

  // =========================
  // Acciones: abrir modales
  // =========================
  const abrirModalRol = (usuario) => {
    setUsuarioSeleccionado(usuario);
    setNuevoRol(usuario.rol || "");
    setModalRol(true);
  };

  const abrirModalManager = (usuario) => {
    setUsuarioSeleccionado(usuario);
    setNuevoManager(usuario.gerente_id ? String(usuario.gerente_id) : "");
    setModalManager(true);
  };

  const abrirModalEditar = (usuario) => {
    setUsuarioSeleccionado(usuario);
    setNuevoNombre(usuario.nombre || "");
    setNuevoEmail(usuario.email || "");
    setNuevoDni(usuario.dni || "");
    setNuevoApellidos(usuario.apellidos || "");
    setNuevoTelefono(usuario.telefono || "");
    setErrorEmail("");
    setErrorMensaje("");
    setAvatarFileEdit(null);
    setModalEditar(true);
  };

  const cerrarModalEditar = () => {
    if (saving) return;
    setModalEditar(false);
    setUsuarioSeleccionado(null);
    setAvatarFileEdit(null);
    setErrorEmail("");
    setErrorMensaje("");
  };

  const cerrarModalRol = () => {
    if (saving) return;
    setModalRol(false);
    setUsuarioSeleccionado(null);
  };

  const cerrarModalManager = () => {
    if (saving) return;
    setModalManager(false);
    setUsuarioSeleccionado(null);
  };

  // =========================
  // Acciones API
  // =========================
  const actualizarRol = async () => {
    if (!usuarioSeleccionado?.id || !nuevoRol) return;

    setSaving(true);
    try {
      const res = await fetch(`${Global.url}usuarios/rol/${usuarioSeleccionado.id}`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
        body: JSON.stringify({ rol: nuevoRol }),
      });

      if (!res.ok) {
        toast.error("No se pudo actualizar el rol.");
        return;
      }

      toast.success("Rol actualizado.");
      await fetchUsuarios();
      cerrarModalRol();
    } catch (e) {
      console.error(e);
      toast.error("Error de conexión al actualizar el rol.");
    } finally {
      setSaving(false);
    }
  };

  const asignarManager = async () => {
    if (!usuarioSeleccionado?.id) return;

    setSaving(true);
    try {
      const res = await fetch(`${Global.url}usuarios/manager/${usuarioSeleccionado.id}`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
        body: JSON.stringify({ gerente_id: nuevoManager || null }),
      });

      if (!res.ok) {
        toast.error("No se pudo asignar el manager.");
        return;
      }

      toast.success("Manager actualizado.");
      await fetchUsuarios();
      cerrarModalManager();
    } catch (e) {
      console.error(e);
      toast.error("Error de conexión al asignar el manager.");
    } finally {
      setSaving(false);
    }
  };

  const validarEmail = (email) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);

  const onAvatarEditChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) {
      setAvatarFileEdit(null);
      return;
    }
    if (!file.type.startsWith("image/")) {
      setErrorMensaje("El archivo seleccionado no es una imagen.");
      setAvatarFileEdit(null);
      return;
    }
    if (file.size > 3 * 1024 * 1024) {
      setErrorMensaje("La imagen no puede superar los 3MB.");
      setAvatarFileEdit(null);
      return;
    }
    setErrorMensaje("");
    setAvatarFileEdit(file);
  };

  const editarUsuario = async () => {
    if (!usuarioSeleccionado?.id) return;

    setErrorEmail("");
    setErrorMensaje("");

    const dniLimpio = (nuevoDni || "").toUpperCase().replace(/\s|-/g, "");
    const emailLimpio = (nuevoEmail || "").trim();
    const emailFinal = emailLimpio === "" ? null : emailLimpio;

    // Validación dinámica según versión
    if (REQUIRE_MAIL) {
      if (!emailFinal) {
        setErrorMensaje("El email es obligatorio en esta versión.");
        return;
      }
      if (!validarEmail(emailFinal)) {
        setErrorEmail("Correo electrónico no válido");
        return;
      }
    } else {
      if (!dniLimpio) {
        setErrorMensaje("El DNI es obligatorio en esta versión.");
        return;
      }
      if (emailFinal && !validarEmail(emailFinal)) {
        setErrorEmail("Correo electrónico no válido");
        return;
      }
    }

    const payload = {
      nombre: nuevoNombre,
      apellidos: (nuevoApellidos || "").trim() || null,
      dni: dniLimpio || null,
      email: emailFinal,
      telefono: (nuevoTelefono || "").trim() || null,
    };

    setSaving(true);
    try {
      // 1) Actualizar datos básicos
      const res = await fetch(`${Global.url}usuarios/editar/${usuarioSeleccionado.id}`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
        body: JSON.stringify(payload),
      });

      if (!res.ok) {
        toast.error("No se pudo editar el usuario.");
        return;
      }

      // 2) Si hay un avatar nuevo seleccionado, subirlo
      if (avatarFileEdit) {
        try {
          const fd = new FormData();
          fd.append("avatar", avatarFileEdit);

          const avatarRes = await fetch(`${Global.url}usuarios/${usuarioSeleccionado.id}/avatar`, {
            method: "POST",
            headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
            body: fd,
          });

          const avatarData = await avatarRes.json().catch(() => ({}));
          if (!avatarRes.ok) {
            console.error("Error al subir avatar en edición:", avatarData);
            toast.warning("Usuario guardado, pero no se pudo subir el avatar.");
          }
        } catch (err) {
          console.error("Error de red al subir avatar (edición):", err);
          toast.warning("Usuario guardado, pero falló la subida del avatar.");
        }
      }

      toast.success("Usuario actualizado.");
      await fetchUsuarios();
      cerrarModalEditar();
    } catch (e) {
      console.error(e);
      toast.error("Error de conexión al editar el usuario.");
    } finally {
      setSaving(false);
    }
  };

  const eliminarUsuario = async (id) => {
    if (!window.confirm("¿Eliminar este usuario?")) return;

    setSaving(true);
    try {
      const res = await fetch(`${Global.url}usuarios/${id}`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
      });

      if (!res.ok) {
        toast.error("No se pudo eliminar el usuario.");
        return;
      }

      toast.success("Usuario desactivado.");
      await fetchUsuarios();
    } catch (e) {
      console.error(e);
      toast.error("Error de conexión al eliminar el usuario.");
    } finally {
      setSaving(false);
    }
  };

  const reactivarUsuario = async (id) => {
    if (!window.confirm("¿Reactivar este usuario?")) return;

    setSaving(true);
    try {
      const res = await fetch(`${Global.url}usuarios/reactivar/${id}`, {
        method: "PUT",
        headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
      });

      if (!res.ok) {
        toast.error("No se pudo reactivar el usuario.");
        return;
      }

      toast.success("Usuario reactivado.");
      await fetchUsuarios();
    } catch (e) {
      console.error(e);
      toast.error("Error de conexión al reactivar el usuario.");
    } finally {
      setSaving(false);
    }
  };

  // =========================
  // DataGrid: rows + columns
  // =========================
  const rows = useMemo(() => {
    return (usuariosOrdenados || []).map((u) => ({
      ...u,
      usuario_full: `${u.nombre || ""} ${u.apellidos || ""}`.trim(),
      activo_txt: u.activo === false ? "Desactivado" : "",
    }));
  }, [usuariosOrdenados]);

  const columns = useMemo(
    () => [
      {
        field: "avatar",
        headerName: "",
        width: 70,
        sortable: false,
        filterable: false,
        renderCell: (params) => {
          const u = params.row;
          const url = getAvatarUrl(u);
          return (
            <Avatar
              src={url || undefined}
              alt={`${u.nombre || ""} ${u.apellidos || ""}`.trim()}
              sx={{ width: 36, height: 36, fontWeight: 700 }}
            >
              {getIniciales(u)}
            </Avatar>
          );
        },
      },
      { field: "nombre", headerName: "Nombre", flex: 1, minWidth: 160 },
      { field: "apellidos", headerName: "Apellidos", flex: 1, minWidth: 180 },
      { field: "email", headerName: "Email", flex: 1, minWidth: 220 },
      { field: "dni", headerName: "DNI", minWidth: 130 },
      { field: "telefono", headerName: "Teléfono", minWidth: 150 },
      { field: "rol", headerName: "Rol", minWidth: 140 },
      { field: "manager", headerName: "Manager", flex: 1, minWidth: 200 },
      {
        field: "acciones",
        headerName: "Acciones",
        minWidth: 450,
        sortable: false,
        renderCell: (params) => {
          const u = params.row;
          return (
            <Stack direction="row" spacing={1} sx={{ alignItems: "center", height: "100%" }}>
              <Button size="small" onClick={() => abrirModalRol(u)}>
                Editar rol
              </Button>
              <Button size="small" onClick={() => abrirModalManager(u)}>
                Asignar manager
              </Button>
              <Button size="small" onClick={() => abrirModalEditar(u)}>
                Modificar
              </Button>

              {u.activo !== false ? (
                <Button size="small" onClick={() => eliminarUsuario(u.id)}>
                  Eliminar
                </Button>
              ) : (
                <Button size="small" onClick={() => reactivarUsuario(u.id)}>
                  Reactivar
                </Button>
              )}
            </Stack>
          );
        },
      },
      ...(mostrarDesactivados
        ? [{ field: "activo_txt", headerName: "Desactivado", minWidth: 140 }]
        : []),
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [mostrarDesactivados, usuariosLocal, saving]
  );

  // =========================
  // Render
  // =========================
  return (
    <Box className="appContainer appContainer--wide">
      <Stack spacing={2}>
        <Typography variant="h5" sx={{ fontWeight: 700 }}>
          Gestión de Usuarios
        </Typography>

        {/* =========================
            Filtros (2 filas compactas)
           ========================= */}
        <Paper sx={{ p: 2 }}>
          <Box
            sx={{
              display: "grid",
              gap: 2,
              gridTemplateColumns: { xs: "1fr", md: "repeat(12, 1fr)" },
              alignItems: "end",
            }}
          >
            {/* Usuario */}
            <Box sx={{ gridColumn: { xs: "auto", md: "span 5" } }}>
              <Autocomplete
                size="small"
                options={usuarioOptions}
                value={filtroUsuario}
                onChange={(_, v) => setFiltroUsuario(v)}
                isOptionEqualToValue={(a, b) => String(a?.value) === String(b?.value)}
                getOptionLabel={(o) => o?.label || ""}
                renderInput={(params) => <TextField {...params} label="Usuario" placeholder="Todos" />}
                clearOnEscape
              />
            </Box>

            {/* Rol */}
            <Box sx={{ gridColumn: { xs: "auto", md: "span 3" } }}>
              <Autocomplete
                size="small"
                options={rolOptions}
                value={selectedRolOption}
                onChange={(_, v) => setFiltroRol(v?.value || "")}
                isOptionEqualToValue={(a, b) => String(a?.value) === String(b?.value)}
                getOptionLabel={(o) => o?.label || ""}
                renderInput={(params) => <TextField {...params} label="Rol" placeholder="Todos" />}
                clearOnEscape
              />
            </Box>

            {/* Manager */}
            <Box sx={{ gridColumn: { xs: "auto", md: "span 3" } }}>
              <Autocomplete
                size="small"
                options={managerOptions}
                value={filtroManager}
                onChange={(_, v) => setFiltroManager(v)}
                isOptionEqualToValue={(a, b) => String(a?.value) === String(b?.value)}
                getOptionLabel={(o) => o?.label || ""}
                renderInput={(params) => <TextField {...params} label="Manager" placeholder="Todos" />}
                clearOnEscape
              />
            </Box>

            {/* Mostrar desactivados */}
            <Box sx={{ gridColumn: { xs: "auto", md: "span 1" } }}>
              <FormControlLabel
                control={
                  <Switch
                    checked={mostrarDesactivados}
                    onChange={(e) => setMostrarDesactivados(e.target.checked)}
                  />
                }
                label="Desactivados"
              />
            </Box>
          </Box>
        </Paper>

        {/* =========================
            Tabla
           ========================= */}
        <Paper sx={{ height: 680, p: 1 }}>
          <DataGrid
            rows={rows}
            columns={columns}
            getRowId={(row) => row.id}
            disableRowSelectionOnClick
            loading={loading}
            getRowClassName={(params) => (params.row.activo === false ? "row--disabled" : "")}
            pagination
            initialState={{
              pagination: { paginationModel: { page: 0, pageSize: 25 } },
              sorting: {
                sortModel: [{ field: orden.campo, sort: orden.ascendente ? "asc" : "desc" }],
              },
            }}
            onSortModelChange={(model) => {
              const m = model?.[0];
              if (!m?.field) return;
              setOrden({ campo: m.field, ascendente: m.sort !== "desc" });
            }}
            pageSizeOptions={[10, 25, 50, 100]}
            slots={{
              noRowsOverlay: () => (
                <Box sx={{ p: 2, color: "text.secondary" }}>
                  {loading ? "Cargando…" : "No hay usuarios con los filtros actuales."}
                </Box>
              ),
            }}
            sx={{
              "& .MuiDataGrid-columnHeaders": {
                borderBottom: "1px solid rgba(0,0,0,0.08)",
              },
              "& .MuiDataGrid-cell": {
                borderBottom: "1px solid rgba(0,0,0,0.06)",
              },
            }}
          />
        </Paper>

        {/* =========================
            MODAL: Editar usuario
           ========================= */}
        <Dialog open={modalEditar && !!usuarioSeleccionado} onClose={cerrarModalEditar} fullWidth maxWidth="sm">
          <DialogTitle>Editar usuario</DialogTitle>
          <Divider />
          <DialogContent sx={{ pt: 2 }}>
            <Stack spacing={2}>
              <Typography variant="body2" sx={{ color: "text.secondary" }}>
                {usuarioSeleccionado
                  ? `${usuarioSeleccionado.nombre || ""} ${usuarioSeleccionado.apellidos || ""}`.trim()
                  : ""}
              </Typography>

              <TextField
                label={`DNI${REQUIRE_DNI ? " (obligatorio)" : ""}`}
                value={nuevoDni}
                onChange={(e) => setNuevoDni(e.target.value)}
                required={REQUIRE_DNI}
                placeholder="12345678Z"
              />

              <TextField
                label="Nombre"
                value={nuevoNombre}
                onChange={(e) => setNuevoNombre(e.target.value)}
                required
              />

              <TextField
                label="Apellidos"
                value={nuevoApellidos}
                onChange={(e) => setNuevoApellidos(e.target.value)}
              />

              <TextField
                label={`Email${REQUIRE_MAIL ? " (obligatorio)" : ""}`}
                type="email"
                value={nuevoEmail}
                onChange={(e) => setNuevoEmail(e.target.value)}
                required={REQUIRE_MAIL}
                placeholder="usuario@dominio.com"
                error={!!errorEmail}
                helperText={errorEmail || " "}
              />

              <TextField
                label="Teléfono"
                value={nuevoTelefono}
                onChange={(e) => setNuevoTelefono(e.target.value)}
                placeholder="Ej: 600 123 456"
              />

              {/* Avatar */}
              <Stack spacing={1}>
                <Typography variant="body2" sx={{ color: "text.secondary" }}>
                  Foto / Avatar
                </Typography>
                <Button variant="outlined" component="label">
                  Seleccionar imagen
                  <input hidden type="file" accept="image/*" onChange={onAvatarEditChange} />
                </Button>
                {avatarFileEdit && (
                  <Typography variant="caption" sx={{ color: "text.secondary" }}>
                    Seleccionado: {avatarFileEdit.name} ({Math.round(avatarFileEdit.size / 1024)} KB)
                  </Typography>
                )}
              </Stack>

              {errorMensaje && <Typography color="error">{errorMensaje}</Typography>}
            </Stack>
          </DialogContent>

          <DialogActions sx={{ px: 3, pb: 2 }}>
            <Button variant="outlined" onClick={cerrarModalEditar} disabled={saving}>
              Cancelar
            </Button>
            <Button onClick={editarUsuario} disabled={saving}>
              {saving ? "Guardando..." : "Guardar"}
            </Button>
          </DialogActions>
        </Dialog>

        {/* =========================
            MODAL: Cambiar rol
           ========================= */}
        <Dialog open={modalRol && !!usuarioSeleccionado} onClose={cerrarModalRol} fullWidth maxWidth="xs">
          <DialogTitle>Cambiar rol</DialogTitle>
          <Divider />
          <DialogContent sx={{ pt: 2 }}>
            <Stack spacing={2}>
              <Typography variant="body2" sx={{ color: "text.secondary" }}>
                {usuarioSeleccionado ? `Usuario: ${usuarioSeleccionado.nombre}` : ""}
              </Typography>

              <TextField
                select
                label="Rol"
                value={nuevoRol}
                onChange={(e) => setNuevoRol(e.target.value)}
              >
                <MenuItem value="usuario">Usuario</MenuItem>
                <MenuItem value="gerente">Gerente</MenuItem>
                <MenuItem value="admin">Administrador</MenuItem>
              </TextField>
            </Stack>
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2 }}>
            <Button variant="outlined" onClick={cerrarModalRol} disabled={saving}>
              Cancelar
            </Button>
            <Button onClick={actualizarRol} disabled={saving || !nuevoRol}>
              {saving ? "Guardando..." : "Guardar"}
            </Button>
          </DialogActions>
        </Dialog>

        {/* =========================
            MODAL: Asignar manager
           ========================= */}
        <Dialog
          open={modalManager && !!usuarioSeleccionado}
          onClose={cerrarModalManager}
          fullWidth
          maxWidth="xs"
        >
          <DialogTitle>Asignar manager</DialogTitle>
          <Divider />
          <DialogContent sx={{ pt: 2 }}>
            <Stack spacing={2}>
              <Typography variant="body2" sx={{ color: "text.secondary" }}>
                {usuarioSeleccionado ? `Usuario: ${usuarioSeleccionado.nombre}` : ""}
              </Typography>

              <TextField
                select
                label="Manager"
                value={nuevoManager}
                onChange={(e) => setNuevoManager(e.target.value)}
              >
                <MenuItem value="">Sin manager</MenuItem>
                {(usuariosLocal || [])
                  .filter((u) => u.rol === "gerente")
                  .map((g) => (
                    <MenuItem key={g.id} value={String(g.id)}>
                      {g.nombre} {g.apellidos || ""}
                    </MenuItem>
                  ))}
              </TextField>
            </Stack>
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2 }}>
            <Button variant="outlined" onClick={cerrarModalManager} disabled={saving}>
              Cancelar
            </Button>
            <Button onClick={asignarManager} disabled={saving}>
              {saving ? "Guardando..." : "Guardar"}
            </Button>
          </DialogActions>
        </Dialog>
      </Stack>
    </Box>
  );
};
